import { format } from 'date-fns';
import { CheckCircle2, Clock, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import {
  useDeferProviderIdentityMutation,
  useResolveProviderIdentityMutation,
} from '@/services/api/treatmentService';
import { ProviderIdentityLifecycleStatus } from '@/services/types/treatment-types';

interface IdentityStatusBadgeProps {
  status?: ProviderIdentityLifecycleStatus | null;
  matterId?: string;
  providerId?: string;
  identityDueAt?: string | null;
  identityResolvedAt?: string | null;
  identityDeferReason?: string | null;
  className?: string;
}

const statusConfig: Record<
  ProviderIdentityLifecycleStatus,
  { bg: string; text: string; label: string; icon: typeof Clock }
> = {
  [ProviderIdentityLifecycleStatus.PENDING]: {
    bg: 'bg-sky-100',
    text: 'text-sky-800',
    label: 'Identity Pending',
    icon: Clock,
  },
  [ProviderIdentityLifecycleStatus.RESOLVED]: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    label: 'Identity Verified',
    icon: ShieldCheck,
  },
  [ProviderIdentityLifecycleStatus.DEFERRED]: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    label: 'Identity Deferred',
    icon: Clock,
  },
  [ProviderIdentityLifecycleStatus.OVERDUE]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    label: 'Identity Overdue',
    icon: ShieldAlert,
  },
};

export const IdentityStatusBadge = ({
  status,
  matterId,
  providerId,
  identityDueAt,
  identityResolvedAt,
  identityDeferReason,
  className,
}: IdentityStatusBadgeProps) => {
  const [deferReason, setDeferReason] = useState('');
  const [showDeferForm, setShowDeferForm] = useState(false);

  const [resolveIdentity, { isLoading: isResolving }] =
    useResolveProviderIdentityMutation();
  const [deferIdentity, { isLoading: isDeferring }] =
    useDeferProviderIdentityMutation();

  if (!status) return null;

  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;
  const canInteract = Boolean(matterId && providerId);
  const isActionable =
    status !== ProviderIdentityLifecycleStatus.RESOLVED && canInteract;

  const handleResolve = async () => {
    if (!matterId || !providerId) return;
    try {
      await resolveIdentity({ matterId, providerId }).unwrap();
      toast.success('Identity verified');
    } catch {
      toast.error('Failed to verify identity');
    }
  };

  const handleDefer = async () => {
    if (!matterId || !providerId || !deferReason.trim()) return;
    try {
      await deferIdentity({
        matterId,
        providerId,
        reason: deferReason.trim(),
        deferDays: 14,
      }).unwrap();
      toast.success('Identity deferred');
      setDeferReason('');
      setShowDeferForm(false);
    } catch {
      toast.error('Failed to defer identity');
    }
  };

  const badge = (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${config.bg} ${config.text} ${isActionable ? 'cursor-pointer hover:opacity-80' : ''} ${className ?? ''}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );

  // Non-interactive: no matterId/providerId passed, or already resolved
  if (!canInteract) return badge;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="inline-flex" onClick={(e) => e.stopPropagation()}>
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setShowDeferForm(false);
          setDeferReason('');
        }
      }}
    >
      <PopoverTrigger asChild>{badge}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <div className="space-y-3">
          {/* Status header */}
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${config.text}`} />
            <span className="text-sm font-medium text-gray-900">
              {config.label}
            </span>
          </div>

          {/* Context info */}
          <div className="space-y-1 text-xs text-gray-600">
            {identityDueAt && (
              <p>
                <span className="font-medium">Due:</span>{' '}
                {format(new Date(identityDueAt), 'MMM d, yyyy')}
              </p>
            )}
            {identityResolvedAt && (
              <p>
                <span className="font-medium">Verified:</span>{' '}
                {format(new Date(identityResolvedAt), 'MMM d, yyyy')}
              </p>
            )}
            {identityDeferReason && (
              <p>
                <span className="font-medium">Defer reason:</span>{' '}
                {identityDeferReason}
              </p>
            )}
          </div>

          {/* Actions for non-RESOLVED statuses */}
          {isActionable && (
            <div className="space-y-2 border-t border-gray-200 pt-2">
              <Button
                size="sm"
                className="w-full"
                onClick={handleResolve}
                disabled={isResolving || isDeferring}
              >
                {isResolving ? (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-3 w-3" />
                )}
                Confirm Identity
              </Button>

              {!showDeferForm ? (
                <button
                  type="button"
                  onClick={() => setShowDeferForm(true)}
                  disabled={isResolving || isDeferring}
                  className="w-full text-xs font-medium text-gray-600 hover:text-gray-900"
                >
                  Defer verification...
                </button>
              ) : (
                <div className="space-y-2">
                  <Textarea
                    value={deferReason}
                    onChange={(e) => setDeferReason(e.target.value)}
                    placeholder="Reason for deferring..."
                    className="min-h-16 text-xs"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      onClick={handleDefer}
                      disabled={
                        !deferReason.trim() || isResolving || isDeferring
                      }
                    >
                      {isDeferring && (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      )}
                      Defer 14 days
                    </Button>
                    <PopoverClose asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs"
                        onClick={() => {
                          setShowDeferForm(false);
                          setDeferReason('');
                        }}
                      >
                        Cancel
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Resolved state: just informational */}
          {status === ProviderIdentityLifecycleStatus.RESOLVED && (
            <div className="border-t border-gray-200 pt-2">
              <p className="text-xs font-medium text-emerald-700">
                Identity has been verified. No action needed.
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
    </div>
  );
};
